import { ensureProtoProps } from '@rehaje/shared'

import { dispatch } from '../dispatch'
import { FatalDecoratorExistedError } from '../errors'
import { ObserveSetEvent } from '../event'
import { DEFINED, REF } from '../flags'
import { addParent, removeParent } from '../operations/parent'
import { batch } from '../operations/register'
import { evalContext, Signal, untrackDepth } from '../signal'
import { undispatchDepth } from '../undispatch'

import type {
  AnnotationOptions,
  ReactivePrototype,
  ValueAnnotation
} from '../types'

class Ref extends Signal {
  __rr_flags = REF
  override _key: string
  _observe: boolean

  _parent: any

  override get value() {
    const val = super.value
    if (undispatchDepth === 0 && this._observe) {
      addParent(this._parent, this._key, val)
    }
    return val?.__rr_deep ?? val?.__rr_shallow ?? val
  }

  override set value(value: any) {
    batch(() => {
      let event
      if (undispatchDepth === 0 && this._observe) {
        event = dispatch(new ObserveSetEvent(this._parent, this._key, value))
        value = event.source.value
      }
      const oldVal = super.peek()
      if (value === oldVal) return
      super.value = value
      if (undispatchDepth === 0 && this._observe) {
        event?.__r_runEffects()
        addParent(this._parent, this._key, value)
        removeParent(this._parent, this._key, oldVal)
      }
    })
  }

  constructor(
    value?: any,
    options: AnnotationOptions = {},
    parent?: any,
    key?: string
  ) {
    super(value, key)
    this._observe = options.observe ?? true
    this._parent = parent ?? this
    this._key ??= 'value'
  }
}

export function createRefDescriptor(
  proto: any,
  key: string,
  options?: AnnotationOptions
): PropertyDescriptor

export function createRefDescriptor(
  proto: ReactivePrototype | undefined,
  key: string,
  options?: AnnotationOptions
): PropertyDescriptor {
  if (proto !== undefined) {
    const signalKeys = ensureProtoProps(proto, '__rr_signal_keys', [])
    if (!signalKeys.includes(key)) signalKeys.push(key)

    const reactiveKeys = ensureProtoProps(proto, '__rr_reactive_keys', [])
    if (!reactiveKeys.includes(key)) reactiveKeys.push(key)

    const observeKeys = ensureProtoProps(proto, '__rr_observe_keys', [])
    if (options?.observe !== false && !observeKeys.includes(key))
      observeKeys.push(key)
  }

  return {
    configurable: false,
    enumerable: true,
    get: new Function(
      'isActiveSignal',
      'Ref',
      'options',
      `
      return function () {
        if (this.__rr_signals_${key} === undefined && isActiveSignal(this.__rr_flags)) {
          this.__rr_signals_${key} = new Ref(this.__rr_values_${key}, options, this, "${key}")
          if (this.__rr_values_${key} !== undefined) {
            this.__rr_values_${key} = undefined
          }
        }
        if (this.__rr_signals_${key} !== undefined) {
          return this.__rr_signals_${key}.value
        } else {
          return this.__rr_values_${key}
        }
      }
      `
    )(isActiveSignal, Ref, options),
    set: new Function(
      'isActiveSignal',
      'Ref',
      'options',
      `
      return function (value) {
        if (this.__rr_signals_${key} === undefined && isActiveSignal(this.__rr_flags)) {
          this.__rr_signals_${key} = new Ref(undefined, options, this, "${key}")
          if (this.__rr_values_${key} !== undefined) {
            this.__rr_values_${key} = undefined
          }
        }
        if (this.__rr_signals_${key} !== undefined) {
          this.__rr_signals_${key}.value = value
        } else {
          this.__rr_values_${key} = value
        }
      }
      `
    )(isActiveSignal, Ref, options)
  }
}
function isActiveSignal(flags: number) {
  return (evalContext !== undefined || untrackDepth === 0) && flags & DEFINED
}

export const ref: ValueAnnotation<AnnotationOptions> = (value, options) => {
  return new Ref(value, options)
}

ref._name = 'ref'

ref._tsDecorator = (proto, key, descriptor, options) => {
  if (descriptor !== undefined)
    throw new FatalDecoratorExistedError(ref._name, key)
  return createRefDescriptor(proto, key, options)
}

ref._esAccessorDecorator = (context, options) => {
  // 在 es 装饰器中，无法在 context 中获取类原型，但可以在 init 的回调中访问类实例和原型
  const descriptor = createRefDescriptor(
    undefined,
    String(context.name),
    options
  )
  return {
    get: descriptor.get,
    init: descriptor.set,
    set: descriptor.set
  }
}
